interface PageTitleOptions {
  pageName: string;
  isHomePage?: boolean;
  isTopLevelPage?: boolean;
  brandFirst?: boolean;
  isMainNavPage?: boolean; // For main navigation pages that use "MojPosao - something" format
}

/**
 * Composable for formatting page titles according to SEO best practices
 *
 * TITLE FORMAT STANDARD: All Borg sites use "{page name} | {site name}" format for better SEO.
 * Use isHomePage: true or brandFirst: true only when brand recognition is more important than content.
 *
 * @param options Configuration for page title formatting
 * @returns Formatted page title string
 */
export const usePageTitle = (options: PageTitleOptions): string => {
  const config = useRuntimeConfig();

  // Get site name from project configuration
  // This will be loaded from the brand-specific project.json
  const siteName = (config.public.project as any)?.title || (config.public.project as any)?.name || 'Site';

  const { pageName, isHomePage = false, isTopLevelPage = false, brandFirst = false, isMainNavPage = false } = options;

  // Main navigation pages: Use "MojPosao - something" format
  // This includes: homepage, /poslovi, /poslodavci, /kalkulator/*, /savjeti
  if (isMainNavPage) {
    return `${siteName} - ${pageName}`;
  }

  // Home page: Use only the tagline/description if provided
  // This avoids duplication when pageName contains the site name
  if (isHomePage) {
    // If pageName doesn't already contain the site name, add it
    // Otherwise just use the pageName as is
    if (pageName && !pageName.includes(siteName)) {
      return `${siteName} | ${pageName}`;
    }
    return pageName || siteName;
  }

  // Top-level pages or pages where brand is most important: Brand name first
  if (isTopLevelPage || brandFirst) {
    return `${siteName} | ${pageName}`;
  }

  // Default format: Page name first for better SEO
  return `${pageName} | ${siteName}`;
};

/**
 * Composable that sets the page title using useHead
 * This is a convenience wrapper that combines usePageTitle with useHead
 *
 * @param options Configuration for page title formatting
 */
export const usePageHead = (options: PageTitleOptions & { description?: string }) => {
  const title = usePageTitle(options);
  const { description } = options;

  useHead({
    title,
    meta: description ? [{ name: 'description', content: description }] : [],
  });

  // Also set OG tags for social sharing
  if (title || description) {
    useSeoMeta({
      ogTitle: title,
      ogDescription: description,
    });
  }

  return title;
};
